import * as JurorRepository from './repositories/JurorRepository';
import { schedulePendingQueueProcess } from './sync/SyncEngine';
import { getDatabase } from './index';

/**
 * UndoManager — session-scoped undo/redo for juror mutations.
 *
 * Design (local-first + collaboration safe):
 * - Each user action records an entry of inverse patches:
 *   { type: 'update', jurorId, before: {field: old}, after: {field: new} }
 *   { type: 'delete', jurorId, snapshot }  (undo recreates, redo re-deletes)
 * - Undo/redo applies patches as NEW mutations through the normal
 *   repository -> pendingOperations -> sync path. Offline they queue like
 *   any edit; collaborators receive them as ordinary updates. No special
 *   server support is needed.
 * - Collaboration guard: a field is only reverted if it still holds the
 *   value this user set (current === expected). Fields changed by someone
 *   else in the meantime are skipped, so undo never destroys newer remote
 *   work. This is per-field selective undo, the same policy used by
 *   collaborative editors.
 * - Stacks are per-case and in-memory only; they intentionally do not
 *   survive a reload (replaying stale patches after a long gap is more
 *   dangerous than useful).
 */

const MAX_STACK = 50;

// caseId -> { undo: [entry], redo: [entry] }
const stacks = new Map();
const listeners = new Set();
let version = 0;
let busy = false;

const IGNORED_FIELDS = new Set([
  '_id', '_localId', '_localOnly', '_synced', '_encrypted',
  'revision', 'updated', 'created', 'caseId', 'id', 'error',
]);

function getStacks(caseId) {
  if (!stacks.has(caseId)) {
    stacks.set(caseId, { undo: [], redo: [] });
  }
  return stacks.get(caseId);
}

function emit() {
  version++;
  for (const listener of listeners) {
    listener();
  }
}

function same(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Resolve a juror by id, falling back to the _localId lookup for jurors
 * whose Dexie key was remapped from a local UUID to a server id after the
 * patch was recorded.
 */
async function resolveJuror(jurorId) {
  const juror = await JurorRepository.getById(jurorId);
  if (juror) return juror;

  const db = getDatabase();
  if (!db) return null;
  return db.jurors.filter(j => j._localId === jurorId).first();
}

/**
 * Build an update op by diffing intended updates against the live juror.
 * Returns null when nothing relevant changes.
 */
export async function captureUpdateOp(jurorId, updates) {
  const existing = await resolveJuror(jurorId);
  if (!existing) return null;

  const before = {};
  const after = {};
  let changed = false;

  for (const key of Object.keys(updates)) {
    if (IGNORED_FIELDS.has(key)) continue;
    if (!same(existing[key], updates[key])) {
      before[key] = existing[key] === undefined ? null : existing[key];
      after[key] = updates[key];
      changed = true;
    }
  }

  if (!changed) return null;
  return { type: 'update', jurorId: existing._id, caseId: existing.caseId, before, after };
}

/** Record an undoable entry. New actions clear the redo stack. */
export function record(caseId, label, ops) {
  const validOps = (ops || []).filter(Boolean);
  if (!caseId || validOps.length === 0) return;

  const s = getStacks(caseId);
  s.undo.push({ label, ops: validOps });
  if (s.undo.length > MAX_STACK) {
    s.undo.shift();
  }
  s.redo.length = 0;
  emit();
}

/**
 * Apply an entry's ops in the given direction.
 * @returns {{ applied: number, skipped: number }}
 */
async function applyOps(ops, direction) {
  const changes = [];
  let applied = 0;
  let skipped = 0;

  for (const op of ops) {
    if (op.type === 'delete') {
      if (direction === 'undo') {
        // Recreate from snapshot. The juror gets a fresh local id and syncs
        // to the server as a new create; the op remembers the new id so a
        // redo can delete the recreated record.
        const snapshot = { ...op.snapshot };
        for (const field of ['_id', '_localId', '_localOnly', '_synced', '_encrypted', 'revision']) {
          delete snapshot[field];
        }
        const created = await JurorRepository.create(op.caseId, snapshot);
        op.recreatedId = created._id;
        applied++;
      } else {
        const current = await resolveJuror(op.recreatedId || op.jurorId);
        if (current) {
          await JurorRepository.remove(current._id);
          applied++;
        } else {
          skipped++;
        }
      }
      continue;
    }

    // type 'update'
    const target = direction === 'undo' ? op.before : op.after;
    const expected = direction === 'undo' ? op.after : op.before;

    const current = await resolveJuror(op.jurorId);
    if (!current) {
      skipped++;
      continue;
    }

    const updates = {};
    for (const key of Object.keys(target)) {
      if (same(current[key], expected[key])) {
        updates[key] = target[key];
      } else {
        // Someone else changed this field since — leave their value alone.
        skipped++;
      }
    }

    if (Object.keys(updates).length > 0) {
      changes.push({ jurorId: current._id, updates });
      applied++;
    }
  }

  if (changes.length > 0) {
    await JurorRepository.updateMany(changes);
  }
  schedulePendingQueueProcess();

  return { applied, skipped };
}

export async function undo(caseId) {
  const s = getStacks(caseId);
  if (busy || s.undo.length === 0) return null;

  busy = true;
  try {
    const entry = s.undo.pop();
    const result = await applyOps(entry.ops, 'undo');
    s.redo.push(entry);
    emit();
    return { label: entry.label, ...result };
  } finally {
    busy = false;
  }
}

export async function redo(caseId) {
  const s = getStacks(caseId);
  if (busy || s.redo.length === 0) return null;

  busy = true;
  try {
    const entry = s.redo.pop();
    const result = await applyOps(entry.ops, 'redo');
    s.undo.push(entry);
    emit();
    return { label: entry.label, ...result };
  } finally {
    busy = false;
  }
}

export function canUndo(caseId) {
  return (stacks.get(caseId)?.undo.length || 0) > 0;
}

export function canRedo(caseId) {
  return (stacks.get(caseId)?.redo.length || 0) > 0;
}

export function peekUndoLabel(caseId) {
  const s = stacks.get(caseId);
  return s?.undo[s.undo.length - 1]?.label || null;
}

export function peekRedoLabel(caseId) {
  const s = stacks.get(caseId);
  return s?.redo[s.redo.length - 1]?.label || null;
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getVersion() {
  return version;
}

export default {
  record,
  captureUpdateOp,
  undo,
  redo,
  canUndo,
  canRedo,
  peekUndoLabel,
  peekRedoLabel,
  subscribe,
  getVersion,
};
